import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DownloadStatus, MeasurementDownload, PQEventsState } from "./pqEventsTypes";
import { MeasuringPointGroupDto } from "../../app/apislices/measuringPointGroupApi";
import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-premium";
import { PqStatusInterval } from "../../app/apislices/measurementApi";
import {
  measurementExportApi,
  PostMeasurementExportGetZippedApiArg,
  PostMeasurementExportGetZippedByIdsApiArg,
} from "../../app/apislices/measurementExportApi";
import { combinedApi, getAccessToken } from "../../app/combinedApi";
import { store } from "../../app/store";
import dayjs from "dayjs";

const initialState: PQEventsState = {
  selectedInterval: "OneMonth",
  selectedEventType: "",
  selectedMeasuringPointGroup: undefined,
  selectedMeasuringPoint: "",
  measuringPointsSortModel: [
    { field: "substation", sort: "asc" },
    { field: "switchgear", sort: "asc" },
  ],
  measuringPointsFilterModel: { items: [] },
  eventsSortModel: [{ field: "start", sort: "desc" }],
  eventsFilterModel: { items: [] },
  recordingsSortModel: [{ field: "start", sort: "desc" }],
  recordingsFilterModel: { items: [] },
  eventTypeFilter: [],
  recorderTypeFilter: [],
  downloads: [],
};

const pqEventsSlice = createSlice({
  name: "pqEventsSlice",
  initialState,
  reducers: {
    setSelectedInterval: (state, action: PayloadAction<PqStatusInterval>) => {
      state.selectedInterval = action.payload;
    },
    setSelectedEventType: (state, action: PayloadAction<string>) => {
      state.selectedEventType = action.payload;
    },
    setSelectedMeasuringPointGroup: (state, action: PayloadAction<MeasuringPointGroupDto | undefined>) => {
      state.selectedMeasuringPointGroup = action.payload;
    },
    setSelectedMeasuringPoint: (state, action: PayloadAction<string>) => {
      state.selectedMeasuringPoint = action.payload;
    },
    setMeasuringPointsSortModel: (state, action: PayloadAction<GridSortModel>) => {
      state.measuringPointsSortModel = action.payload;
    },
    setMeasuringPointsFilterModel: (state, action: PayloadAction<GridFilterModel>) => {
      state.measuringPointsFilterModel = action.payload;
    },
    setEventsSortModel: (state, action: PayloadAction<GridSortModel>) => {
      state.eventsSortModel = action.payload;
    },
    setEventsFilterModel: (state, action: PayloadAction<GridFilterModel>) => {
      state.eventsFilterModel = action.payload;
    },
    setRecordingsSortModel: (state, action: PayloadAction<GridSortModel>) => {
      state.recordingsSortModel = action.payload;
    },
    setRecordingsFilterModel: (state, action: PayloadAction<GridFilterModel>) => {
      state.recordingsFilterModel = action.payload;
    },
    setEventTypeFilter: (state, action: PayloadAction<string[]>) => {
      state.eventTypeFilter = action.payload;
    },
    setRecorderTypeFilter: (state, action: PayloadAction<string[]>) => {
      state.recorderTypeFilter = action.payload;
    },
    addDownload: (state, action: PayloadAction<MeasurementDownload>) => {
      state.downloads.push(action.payload);
    },
    setDownloadStatus: (state, action: PayloadAction<[number, DownloadStatus]>) => {
      if (state.downloads.find((d) => d.id === action.payload[0])) {
        state.downloads.find((d) => d.id === action.payload[0]).status = action.payload[1];
        state.downloads.find((d) => d.id === action.payload[0]).changeDate = dayjs().toISOString();
      }
    },
    setDownloadProgess: (state, action: PayloadAction<[number, number]>) => {
      if (state.downloads.find((d) => d.id === action.payload[0])) {
        state.downloads.find((d) => d.id === action.payload[0]).progress = action.payload[1];
      }
    },
    addAbortControllerToDownload: (state, action: PayloadAction<[number, AbortController]>) => {
      if (state.downloads.find((d) => d.id === action.payload[0])) {
        state.downloads.find((d) => d.id === action.payload[0]).abortController = action.payload[1];
      }
    },
  },
});

export const {
  setSelectedInterval,
  setSelectedEventType,
  setSelectedMeasuringPointGroup,
  setSelectedMeasuringPoint,
  setMeasuringPointsSortModel,
  setMeasuringPointsFilterModel,
  setEventsSortModel,
  setEventsFilterModel,
  setRecordingsSortModel,
  setRecordingsFilterModel,
  setEventTypeFilter,
  setRecorderTypeFilter,
  addDownload,
  setDownloadStatus,
  setDownloadProgess,
  addAbortControllerToDownload,
} = pqEventsSlice.actions;

export default pqEventsSlice.reducer;

measurementExportApi.enhanceEndpoints({
  endpoints: {
    postMeasurementExportGetZippedByIds: {
      transformResponse: (response: Blob, meta: unknown, args: PostMeasurementExportGetZippedByIdsApiArg) => {
        downloadFile(response, "stoerschriebe.zip");
        return new Promise(() => "");
      },
      query: (queryArg) => ({
        url: `/MeasurementExport/GetZippedByIds`,
        method: "POST",
        body: queryArg,
        responseHandler: (response) => response.blob(),
        cache: "no-cache",
      }),
      invalidatesTags: [],
    },
    postMeasurementExportGetZipped: {
      transformResponse: (response: Blob, meta: unknown, args: PostMeasurementExportGetZippedApiArg) => {
        downloadFile(response, "stoerschriebe.zip");
        return new Promise(() => "");
      },
      query: (queryArg) => ({
        url: `/MeasurementExport/GetZipped`,
        method: "POST",
        params: {
          from: queryArg["from"],
          to: queryArg.to,
          measuringPoints: queryArg.measuringPoints,
          oscilloscope: queryArg.oscilloscope,
          rms: queryArg.rms,
        },
        responseHandler: (response) => response.blob(),
        cache: "no-cache",
      }),
      invalidatesTags: [],
    },
  },
});

export async function readFileWithFetchAndBlob(url: string, body: string) {
  const baseUrl = import.meta.env.VITE_API_URL;
  const accessToken = await getAccessToken();
  const options = {
    headers: {
      Authorization: "Bearer " + accessToken,
      "Content-Type": "application/json",
    },
    body: body,
    method: "POST",
  };
  fetch(baseUrl + url, options)
    .then((response) => response.blob())
    .then((blob) => {
      downloadFile(blob, "stoerschriebe.zip");
    });
}

export async function readFileWithFetchStream(url: string, body: string, downloadId?: number) {
  const dispatch = store.dispatch;

  const controller = new AbortController();
  const signal = controller.signal;

  var length = 0;
  var responseCode = 0;

  const baseUrl = import.meta.env.VITE_API_URL;
  const accessToken = await getAccessToken();
  const options = {
    headers: {
      Authorization: "Bearer " + accessToken,
      "Content-Type": "application/json",
    },
    body: body,
    method: "POST",
    signal,
  };

  fetch(baseUrl + url, options)
    // Retrieve its body as ReadableStream
    .then((response) => {
      responseCode = response.status;
      const reader = response.body?.getReader();
      return new ReadableStream({
        start(controller) {
          return pump();
          function pump(): any {
            return reader?.read().then(({ done, value }: ReadableStreamReadResult<Uint8Array>) => {
              // When no more data needs to be consumed, close the stream
              if (done) {
                controller.close();
                return;
              }
              // Enqueue the next data chunk into our target stream
              controller.enqueue(value);
              length += value.length;
              if (downloadId) dispatch(setDownloadProgess([downloadId, length / 1024 / 1024]));
              return pump();
            });
          }
        },
      });
    })
    // Create a new response out of the stream
    .then((stream) => new Response(stream))
    .then((response) => response.blob())
    .then((blob) => {
      if (responseCode !== 204) downloadFile(blob, "stoerschriebe.zip");
    })
    .then(() => {
      if (downloadId) {
        if (responseCode === 200) dispatch(setDownloadStatus([downloadId, "success"]));
        if (responseCode === 206) dispatch(setDownloadStatus([downloadId, "partial"]));
        if (responseCode === 204) dispatch(setDownloadStatus([downloadId, "noContent"]));
      }
    })
    .catch(() => {
      if (downloadId) dispatch(setDownloadStatus([downloadId, "timeout"]));
    });
}

const downloadFile = (result: Blob, filename: string) => {
  const hiddenElement = document.createElement("a");
  const url = window.URL || window.webkitURL;
  const blobPDF = url.createObjectURL(result);
  hiddenElement.href = blobPDF;
  hiddenElement.target = "_blank";
  hiddenElement.download = filename;
  hiddenElement.click();
  hiddenElement.remove();
  url.revokeObjectURL(blobPDF);
  return { data: null };
};
